import { useEffect, useState } from "react";
import { api } from "../../lib/api";
import { ConfirmButton, Field, ImageField, MetaLine, SaveButton, SortableList, inputCls } from "../../components/admin/fields";

export default function CVAdminPage() {
  const [page, setPage] = useState(null);
  const [entries, setEntries] = useState([]);
  const [busy, setBusy] = useState(false);
  const [bulkSection, setBulkSection] = useState("");
  const [bulkLines, setBulkLines] = useState("");
  const [bulkMsg, setBulkMsg] = useState("");

  const load = () => {
    api.get("/admin/cv-page").then((res) => setPage(res.data)).catch(() => {});
    api.get("/admin/cv-entries").then((res) => setEntries(res.data || [])).catch(() => {});
  };
  useEffect(load, []);

  if (!page) return <p className="eyebrow">Loading…</p>;

  const setPageField = (key) => (e) => setPage((p) => ({ ...p, [key]: e && e.target ? e.target.value : e }));

  const savePage = async (e) => {
    e.preventDefault();
    setBusy(true);
    await api.put("/admin/cv-page", page);
    setBusy(false);
  };

  const persistOrder = async (ordered) => {
    for (let i = 0; i < ordered.length; i += 1) {
      if (ordered[i].order !== i + 1) {
        await api.put(`/admin/cv-entries/${ordered[i].id}`, { ...ordered[i], order: i + 1 });
      }
    }
  };

  const updateEntry = async (entry) => {
    await api.put(`/admin/cv-entries/${entry.id}`, entry);
  };

  const addEntry = async () => {
    const { data } = await api.post("/admin/cv-entries", { section: "Selected Exhibitions", year: "", text: "" });
    setEntries([...entries, data]);
  };

  const paste = async () => {
    if (!bulkSection.trim() || !bulkLines.trim()) return;
    const { data } = await api.post("/admin/cv-entries/bulk", { section: bulkSection, lines: bulkLines });
    setBulkMsg(`${data.created} entries added.`);
    setBulkLines("");
    load();
  };

  return (
    <div className="max-w-3xl space-y-14" data-testid="admin-cv">
      <form onSubmit={savePage} className="space-y-8">
        <div className="flex items-center justify-between">
          <h1 className="h-condensed text-4xl">CV Page</h1>
          <MetaLine by={page.updated_by} at={page.updated_at} />
        </div>
        <Field label="Artist biography" hint="Separate paragraphs with a blank line. Shown on the right half of the CV page.">
          <textarea rows={12} value={page.biography || ""} onChange={setPageField("biography")} className={inputCls} data-testid="cv-biography" />
        </Field>
        <Field label="Quote" hint="Shown large in Jazmin at the bottom of the page, beside the photo.">
          <textarea rows={4} value={page.quote || ""} onChange={setPageField("quote")} className={inputCls} data-testid="cv-quote" />
        </Field>
        <ImageField label="Quote section photo" value={page.quote_image} onChange={setPageField("quote_image")} alt={page.quote_image_alt} onAltChange={setPageField("quote_image_alt")} testid="cv-quote-image" />
        <div className="grid gap-6 sm:grid-cols-2">
          <Field label="SEO title">
            <input value={page.seo_title || ""} onChange={setPageField("seo_title")} className={inputCls} data-testid="cv-seo-title" />
          </Field>
          <Field label="SEO description">
            <input value={page.seo_description || ""} onChange={setPageField("seo_description")} className={inputCls} data-testid="cv-seo-description" />
          </Field>
        </div>
        <SaveButton busy={busy} dirty testid="cv-page-save" />
      </form>

      <section className="border-t border-charcoal pt-10">
        <div className="flex items-center justify-between">
          <h2 className="h-condensed text-2xl">Entries</h2>
          <button type="button" onClick={addEntry} data-testid="cv-add-entry" className="link-arrow text-xs">
            Add entry
          </button>
        </div>
        <p className="mt-2 text-xs font-light opacity-60">Drag entries to reorder. Save each row after editing.</p>
        <div className="mt-6 border-t border-charcoal">
          <SortableList
            items={entries}
            onChange={setEntries}
            onPersist={() => persistOrder(entries)}
            keyFn={(e) => e.id}
            testid="cv-entries-list"
            renderItem={(entry) => (
              <div className="grid gap-3 border-b border-charcoal py-4 sm:grid-cols-[160px_90px_1fr_auto_auto]" data-testid={`cv-entry-${entry.id}`}>
                <input
                  value={entry.section}
                  onChange={(e) => setEntries(entries.map((x) => (x.id === entry.id ? { ...x, section: e.target.value } : x)))}
                  placeholder="Section"
                  className={inputCls}
                  data-testid={`cv-entry-section-${entry.id}`}
                />
                <input
                  value={entry.year}
                  onChange={(e) => setEntries(entries.map((x) => (x.id === entry.id ? { ...x, year: e.target.value } : x)))}
                  placeholder="Year"
                  className={inputCls}
                  data-testid={`cv-entry-year-${entry.id}`}
                />
                <input
                  value={entry.text}
                  onChange={(e) => setEntries(entries.map((x) => (x.id === entry.id ? { ...x, text: e.target.value } : x)))}
                  placeholder="Entry text"
                  className={inputCls}
                  data-testid={`cv-entry-text-${entry.id}`}
                />
                <button type="button" onClick={() => updateEntry(entry)} className="link-arrow text-xs" data-testid={`cv-entry-save-${entry.id}`}>
                  Save
                </button>
                <ConfirmButton
                  label="Delete"
                  testid={`cv-entry-delete-${entry.id}`}
                  onConfirm={async () => {
                    await api.delete(`/admin/cv-entries/${entry.id}`);
                    setEntries(entries.filter((x) => x.id !== entry.id));
                  }}
                />
              </div>
            )}
          />
        </div>
      </section>

      <section className="border-t border-charcoal pt-10" data-testid="cv-bulk-paste">
        <h2 className="h-condensed text-2xl">Paste in bulk</h2>
        <p className="mt-2 text-xs font-light opacity-60">One entry per line, as &quot;year, text&quot; — for example: 2024, A Bunch Of Stuff, Los Angeles</p>
        <div className="mt-5 space-y-4">
          <Field label="Section">
            <input value={bulkSection} onChange={(e) => setBulkSection(e.target.value)} placeholder="Selected Exhibitions" className={inputCls} data-testid="cv-bulk-section" />
          </Field>
          <Field label="Lines">
            <textarea rows={6} value={bulkLines} onChange={(e) => setBulkLines(e.target.value)} className={inputCls} data-testid="cv-bulk-lines" />
          </Field>
          <div className="flex items-center gap-4">
            <button type="button" onClick={paste} className="border border-charcoal bg-charcoal px-6 py-2.5 font-condensed text-xs font-semibold uppercase tracking-[0.14em] text-offwhite" data-testid="cv-bulk-submit">
              Convert to entries
            </button>
            {bulkMsg && <span className="text-xs font-light opacity-60">{bulkMsg}</span>}
          </div>
        </div>
      </section>
    </div>
  );
}
